import { ReactRenderer } from "@tiptap/react";
import MentionDropdown from "app/components/ReplyComment/components/MentionDropdown";
import { TicketType } from "app/components/ReplyComment/redux/types";
import { IMemberList } from "app/containers/Features/components/MemberSelection/types";
import { ComponentType } from "react";
import tippy from "tippy.js";

export enum MentionType {
  USER = "user",
  TICKET = "ticket",
}

export interface MentionItem {
  type: MentionType;
  user?: IMemberList;
  ticket?: TicketType;
}

export interface MentionCommandItem {
  id: string;
  label: string;
  type: MentionType;
  ticketId?: string;
  ticketTitle?: string;
  ticketTypeName?: string;
  ticketTypeIcon?: string;
  ticketStatusName?: string;
  ticketStatusColor?: string;
  ticketPriority?: string;
  projectId?: string;
  teamId?: string;
}

export interface MentionDropdownComponentProps {
  command: (item: MentionCommandItem) => void;
}

type MentionSource<T> = T[] | (() => T[]);

function resolveSource<T>(source: MentionSource<T>): T[] {
  return typeof source === "function" ? source() || [] : source || [];
}

interface CreateMentionSuggestionOptions {
  dropdownComponent?: ComponentType<MentionDropdownComponentProps>;
}

export const createMentionSuggestion = ({
  dropdownComponent = MentionDropdown,
}: CreateMentionSuggestionOptions) => ({
  render: () => {
    let component;
    let popup;
    let currentProps;
    let updateDropdownPosition: (() => void) | null = null;

    return {
      onStart: (props) => {
        currentProps = props;
        component = new ReactRenderer(dropdownComponent, {
          props,
          editor: props.editor,
          className: "mention-dropdown",
        });

        const getMentionDropdownPosition = () =>
          currentProps.clientRect?.() as DOMRect;

        updateDropdownPosition = () => {
          popup?.[0]?.setProps({
            getReferenceClientRect: getMentionDropdownPosition,
          });
        };

        popup = tippy("body", {
          getReferenceClientRect: getMentionDropdownPosition,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
          offset: [25, 0],
        });

        window.addEventListener("scroll", updateDropdownPosition, true);
        window.addEventListener("resize", updateDropdownPosition);
      },

      onUpdate(props) {
        currentProps = props;
        component.updateProps(props);
        updateDropdownPosition?.();
      },

      onExit() {
        if (updateDropdownPosition) {
          window.removeEventListener(
            "scroll",
            updateDropdownPosition,
            true,
          );
          window.removeEventListener("resize", updateDropdownPosition);
        }
        popup[0].destroy();
        component.destroy();
      },
    };
  },
});
